import React, { useState, useEffect, useCallback } from 'react';
import { PAGE_REMOVAL_TYPES, validateRemovalConfig, getRemovalDescription, parsePageRange, formatPageRange } from '../utils/pdfPageUtils';
import './PDFPageRemover.css';

function PageSelectionControls({ totalPages, removalConfig, onConfigChange, disabled = false }) {
  const [customPageInput, setCustomPageInput] = useState('');
  const [keepPagesInput, setKeepPagesInput] = useState('');
  const [rangeStart, setRangeStart] = useState(1);
  const [rangeEnd, setRangeEnd] = useState(1);
  const [nValue, setNValue] = useState(1);

  // Update inputs when config changes externally
  useEffect(() => {
    switch (removalConfig.type) {
      case PAGE_REMOVAL_TYPES.CUSTOM:
        if (removalConfig.pagesToRemove) {
          setCustomPageInput(formatPageRange(removalConfig.pagesToRemove));
        }
        break;
      case PAGE_REMOVAL_TYPES.KEEP:
        if (removalConfig.pagesToKeep) {
          setKeepPagesInput(formatPageRange(removalConfig.pagesToKeep));
        }
        break;
      default:
        break;
    }
    if (removalConfig.options?.startPage) {
      setRangeStart(removalConfig.options.startPage);
    }
    if (removalConfig.options?.endPage) {
      setRangeEnd(removalConfig.options.endPage);
    }
    if (removalConfig.options?.count) {
      setNValue(removalConfig.options.count);
    }
  }, [removalConfig]);

  const handleTypeChange = useCallback((newType) => {
    let newConfig = { type: newType, options: {} };

    // Clear input fields when switching types for better UX
    if (newType !== PAGE_REMOVAL_TYPES.CUSTOM) {
      setCustomPageInput('');
    }
    if (newType !== PAGE_REMOVAL_TYPES.KEEP) {
      setKeepPagesInput('');
    }

    // Reset range values when switching to range mode for clean start
    if (newType === PAGE_REMOVAL_TYPES.RANGE) {
      setRangeStart(1);
      setRangeEnd(1);
      newConfig.options.startPage = 1;
      newConfig.options.endPage = 1;
    } else if (newType !== PAGE_REMOVAL_TYPES.RANGE) {
      setRangeStart(1);
      setRangeEnd(1);
    }

    switch (newType) {
      case PAGE_REMOVAL_TYPES.FIRST_N:
      case PAGE_REMOVAL_TYPES.LAST_N:
      case PAGE_REMOVAL_TYPES.MIDDLE_N:
        newConfig.options.count = nValue;
        break;

      case PAGE_REMOVAL_TYPES.RANGE:
        // Already set above
        break;

      case PAGE_REMOVAL_TYPES.CUSTOM:
        const parsedCustom = parsePageRange(customPageInput, totalPages);
        newConfig.pagesToRemove = parsedCustom;
        break;

      case PAGE_REMOVAL_TYPES.KEEP:
        const parsedKeep = parsePageRange(keepPagesInput, totalPages);
        newConfig.pagesToKeep = parsedKeep;
        break;

      default:
        // For FIRST and LAST types, no additional options needed
        break;
    }

    onConfigChange(newConfig);
  }, [nValue, customPageInput, keepPagesInput, totalPages, onConfigChange]);


  const handleRangeChange = useCallback((start, end) => {
    setRangeStart(start);
    setRangeEnd(end);
    if (removalConfig.type === PAGE_REMOVAL_TYPES.RANGE) {
      onConfigChange({
        ...removalConfig,
        options: { ...removalConfig.options, startPage: start, endPage: end }
      });
    }
  }, [removalConfig, onConfigChange]);

  const handleCustomPagesChange = useCallback((value) => {
    setCustomPageInput(value);
    if (removalConfig.type === PAGE_REMOVAL_TYPES.CUSTOM) {
      const parsed = parsePageRange(value, totalPages);
      onConfigChange({
        ...removalConfig,
        pagesToRemove: parsed
      });
    }
  }, [removalConfig, totalPages, onConfigChange]);

  const handleKeepPagesChange = useCallback((value) => {
    setKeepPagesInput(value);
    if (removalConfig.type === PAGE_REMOVAL_TYPES.KEEP) {
      const parsed = parsePageRange(value, totalPages);
      onConfigChange({
        ...removalConfig,
        pagesToKeep: parsed
      });
    }
  }, [removalConfig, totalPages, onConfigChange]);

  const isValidConfig = validateRemovalConfig(removalConfig, totalPages);
  const description = getRemovalDescription(removalConfig, totalPages);

  return (
    <div className="page-selection-controls">
      {/* Operation Selection Cards */}
      <div className="page-remover-operation-cards">
        {/* Range Operations Card */}
        <div className={`page-remover-operation-card ${removalConfig.type === PAGE_REMOVAL_TYPES.RANGE ? 'active' : ''}`}>
          <div className="page-remover-card-header">
            <div className="page-remover-card-icon">📄</div>
            <h3>Page Range</h3>
            <p>Remove a continuous range of pages</p>
          </div>

          <label className="page-remover-radio-option">
            <input
              type="radio"
              name="removalType"
              value={PAGE_REMOVAL_TYPES.RANGE}
              checked={removalConfig.type === PAGE_REMOVAL_TYPES.RANGE}
              onChange={() => handleTypeChange(PAGE_REMOVAL_TYPES.RANGE)}
              disabled={disabled || totalPages <= 1}
            />
            <span className="page-remover-radio-content">
              <span className="page-remover-method-title">Remove pages from</span>
              <div className="page-remover-range-inputs">
                <div className="page-remover-input-group">
                  <label>From</label>
                  <input
                    type="number"
                    min="1"
                    max={totalPages}
                    value={rangeStart}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '') {
                        setRangeStart('');
                        return;
                      }
                      const start = parseInt(value);
                      if (!isNaN(start)) {
                        handleRangeChange(start, Math.max(start, rangeEnd || start));
                      }
                    }}
                    onFocus={(e) => e.target.select()}
                    onBlur={(e) => {
                      if (e.target.value === '' || isNaN(parseInt(e.target.value))) {
                        setRangeStart(1);
                        handleRangeChange(1, Math.max(1, rangeEnd || 1));
                      }
                    }}
                    disabled={disabled || removalConfig.type !== PAGE_REMOVAL_TYPES.RANGE}
                    className="page-remover-number-input"
                  />
                </div>
                <div className="page-remover-input-separator">to</div>
                <div className="page-remover-input-group">
                  <label>To</label>
                  <input
                    type="number"
                    min={rangeStart}
                    max={totalPages}
                    value={rangeEnd}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '') {
                        setRangeEnd('');
                        return;
                      }
                      const end = parseInt(value);
                      if (!isNaN(end)) {
                        handleRangeChange(rangeStart || 1, end);
                      }
                    }}
                    onFocus={(e) => e.target.select()}
                    onBlur={(e) => {
                      if (e.target.value === '' || isNaN(parseInt(e.target.value))) {
                        setRangeEnd(rangeStart || 1);
                        handleRangeChange(rangeStart || 1, rangeStart || 1);
                      }
                    }}
                    disabled={disabled || removalConfig.type !== PAGE_REMOVAL_TYPES.RANGE}
                    className="page-remover-number-input"
                  />
                </div>
              </div>
            </span>
          </label>
        </div>

        {/* Advanced Options Card */}
        <div className={`page-remover-operation-card ${(removalConfig.type === PAGE_REMOVAL_TYPES.CUSTOM || removalConfig.type === PAGE_REMOVAL_TYPES.KEEP) ? 'active' : ''}`}>
          <div className="page-remover-card-header">
            <div className="page-remover-card-icon">⚙️</div>
            <h3>Advanced Selection</h3>
            <p>Specify exact pages to remove or keep</p>
          </div>

          <div className="page-remover-advanced-options">
            <label className="page-remover-radio-option">
              <input
                type="radio"
                name="removalType"
                value={PAGE_REMOVAL_TYPES.CUSTOM}
                checked={removalConfig.type === PAGE_REMOVAL_TYPES.CUSTOM}
                onChange={() => handleTypeChange(PAGE_REMOVAL_TYPES.CUSTOM)}
                disabled={disabled}
              />
              <span className="page-remover-radio-content">
                <span className="page-remover-method-title">🗑️ Remove Specific Pages</span>
                {removalConfig.type === PAGE_REMOVAL_TYPES.CUSTOM && (
                  <div className="page-remover-text-input-group">
                    <input
                      type="text"
                      placeholder="e.g., 1,3,5-7,10"
                      value={customPageInput}
                      onChange={(e) => handleCustomPagesChange(e.target.value)}
                      onFocus={(e) => e.target.select()}
                      disabled={disabled}
                      className="page-remover-text-input"
                    />
                    <p className="page-remover-input-help">
                      Enter page numbers separated by commas. Use ranges like 5-7 for consecutive pages.
                    </p>
                  </div>
                )}
              </span>
            </label>

            <label className="page-remover-radio-option">
              <input
                type="radio"
                name="removalType"
                value={PAGE_REMOVAL_TYPES.KEEP}
                checked={removalConfig.type === PAGE_REMOVAL_TYPES.KEEP}
                onChange={() => handleTypeChange(PAGE_REMOVAL_TYPES.KEEP)}
                disabled={disabled}
              />
              <span className="page-remover-radio-content">
                <span className="page-remover-method-title">📋 Keep Only Specific Pages</span>
                {removalConfig.type === PAGE_REMOVAL_TYPES.KEEP && (
                  <div className="page-remover-text-input-group">
                    <input
                      type="text"
                      placeholder="e.g., 2,4,6-8,12"
                      value={keepPagesInput}
                      onChange={(e) => handleKeepPagesChange(e.target.value)}
                      onFocus={(e) => e.target.select()}
                      disabled={disabled}
                      className="page-remover-text-input"
                    />
                    <p className="page-remover-input-help">
                      Enter page numbers to keep. All other pages will be removed.
                    </p>
                  </div>
                )}
              </span>
            </label>
          </div>
        </div>
      </div>

      {/* Operation Preview */}
      <div className="page-remover-operation-preview">
        <div className={`page-remover-preview-content ${isValidConfig ? 'valid' : 'invalid'}`}>
          <div className="page-remover-preview-icon">
            {isValidConfig ? '✅' : '⚠️'}
          </div>
          <div className="page-remover-preview-text">
            <h4>Operation Preview</h4>
            <p>{description}</p>
            {!isValidConfig && (
              <p className="page-remover-error-text">
                Invalid configuration. Please check your selection.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PageSelectionControls;